import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { accessLevelsApi } from '../accessLevelsApi';
import type { AccessLevel } from '../accessLevelTypes';

/** The sentinel the picker uses for "no level", which is a real choice and not a cleared field. */
const NONE = 'none';

/**
 * Tags one account's clearance on ONE module.
 *
 * **"No level" is an option rather than clearing the picker**, because the two are different
 * answers and only one of them is deliberate. An untagged account is not an account whose
 * clearance nobody has decided — it sits at the weakest rung and reads the least — so the form
 * says that out loud rather than leaving a reader to infer it from an empty box.
 *
 * **No Autogenerate, under the documented exemption**: the form is already filled at mount —
 * the picker opens on the account's current rung — so the contract Autogenerate exists to meet
 * is met before the button would have anything to do. The field is also a decision about a
 * named person's access, which is not a thing a generator could fill honestly.
 */
export function SetUserAccessLevelModal({
  open,
  userId,
  displayName,
  moduleKey,
  moduleName,
  currentLevelId,
  onClose,
  onSaved,
}: {
  open: boolean;
  userId: string;
  /** The account's own label, so the form names who is being tagged. */
  displayName: string;
  moduleKey: string;
  moduleName: string;
  /** Null where the account carries no tag for this module. */
  currentLevelId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [levelId, setLevelId] = useState<string>(currentLevelId ?? NONE);

  const [levels, setLevels] = useState<AccessLevel[]>([]);
  const [loadingLevels, setLoadingLevels] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    accessLevelsApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setLevels(next);
      })
      /*
        A failed options fetch leaves the picker empty and the primary disabled by the loading
        flag clearing. It deliberately does NOT call focusValidationError — one call per catch,
        in the save path only.
      */
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoadingLevels(false);
      });

    return () => controller.abort();
  }, []);

  const chosen = levelId === NONE ? null : levelId;
  const canSave = confirmed && !saving && !loadingLevels;

  const chosenLevel = levels.find((l) => l.id === chosen) ?? null;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await accessLevelsApi.setUserLevels(userId, {
        // One module, named explicitly: the request touches only what it speaks about, so
        // this page cannot clear a tag on a module it does not show.
        assignments: [{ moduleKey, accessLevelId: chosen }],
      });

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not set this access level.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'user-access-level' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Set access level"
      size="md"
      id="user-access-level-modal"
      confirm={{
        id: 'user-access-level-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="user-access-level-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            id="user-access-level-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Select
          id="user-access-level-access-level-id"
          label={`${moduleName} access level`}
          description={`The clearance ${displayName} holds when reading ${moduleName} records. A stronger rung reads more.`}
          value={levelId}
          onChange={(next) => setLevelId(next || NONE)}
          loading={loadingLevels}
          disabled={saving}
          // The ladder's own order, strongest first — alphabetically "Level 10" sorts above
          // "Level 2", and the order IS the meaning here.
          sort="none"
          clearable={false}
          options={[
            {
              value: NONE,
              label: 'No level',
              sublabel: 'The weakest clearance: unclassified records only, plus the bottom rung',
            },
            ...levels.map((level) => ({
              value: level.id,
              label: level.name,
              sublabel: level.description ?? undefined,
              right: <span className="font-mono tabular-nums">Rank {level.rank}</span>,
            })),
          ]}
          error={error?.fieldError('accessLevelId')}
        />

        {/*
          The consequence, on the form and before the tick, in the terms of the record rather
          than the model: this is the one control in the product that hands somebody records
          they could not see a moment ago.
        */}
        <p className="text-xs text-slate-500">
          {chosenLevel
            ? `${displayName} will read every ${moduleName} record at ${chosenLevel.name} and weaker, and nothing stronger.`
            : `${displayName} will read unclassified ${moduleName} records and the weakest rung only. Anything classified above it stays out of sight — the list still counts it.`}
        </p>
      </div>
    </Modal>
  );
}
